import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import useTracking from "@repo/shopify/hooks/useTracking";
import { adjustCartLines, getQuantityLimitObj } from "@repo/shopify/src/utils/index";
import _isEmpty from "lodash/isEmpty";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Route, Routes, matchPath, useLocation } from "react-router-dom";

import PageLayout from "@organisms/PageLayout";

import Account from "@pages/Account/Account";
import AccountInformation from "@pages/Account/AccountInformation";
import OrderDetail from "@pages/Account/OrderDetail";
import OrderHistory from "@pages/Account/OrderHistory";
import Checkout from "@pages/Checkout/Checkout";
import OrderCompleted from "@pages/Checkout/OrderCompleted";
import Collections from "@pages/Collections";
import KakaoOauth from "@pages/KakaoOauth";
import Login from "@pages/Login";
import NotFound from "@pages/NotFound";
import Products from "@pages/Products";
import Refresh from "@pages/Refresh";
import Search from "@pages/Search";
import SubPages from "@pages/SubPages";
import Cs from "@pages/SubPages/CustomerService";
import PrivacyPolicy from "@pages/SubPages/PrivacyPolicy";
import ShippingInformation from "@pages/SubPages/ShippingInformation";
import TermsConditions from "@pages/SubPages/TermsConditions";

import useCart from "@hooks/reducerHooks/useCart";
import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useCertification from "@hooks/utilsHooks/useCertification";

import { URLS } from "@const/urls";

import { checkAccessAllowed, checkIsArchiveSalePeriod, getServerTime } from "@utils/archive-sale.ts";

import "@styles/font.css";
import "@styles/index.css";

ChannelService.loadScript();

function App() {
  const [isAccessAllowed, setIsAccessAllowed] = useState(true);
  const { pathname, search } = useLocation();

  const { showFooter } = useLayout();
  const { cart, createCart, createCartWithParams, updateCartLineBase, discountMutate, setCartAttributes } = useCart();
  const { customer, fetchUser, accessToken } = useUser();

  const { gtmInit } = useTracking();

  /** 모바일 환경에서 본인 인증 후 인증 정보 fetch, shopify 유저 정보 업데이트, 로그인 처리 */
  useCertification();

  useEffect(() => {
    // redux에 카트가 비어있으면 새로 만들어서 넣어준다.
    if (!cart || _isEmpty(cart) || !cart.id) {
      createCartWithParams({
        variables: {
          input: {
            attributes: [{ key: "isFamilySale", value: "true" }],
          },
        },
      });
    }

    if (!customer) {
      fetchUser({ variables: { customerAccessToken: accessToken } });
    }
  }, []);

  useEffect(() => {
    if (
      Boolean(matchPath(URLS.FEATURES, pathname)) ||
      Boolean(pathname.startsWith(URLS.DRAW)) ||
      // Boolean(pathname.startsWith(URLS.COLLECTIONS)) ||
      Boolean(pathname.startsWith(URLS.CHECKOUT)) ||
      Boolean(pathname.startsWith(URLS.SEARCH))
    ) {
      showFooter(false);
    } else if (!Boolean(matchPath(URLS.ACCOUNT_ORDER_HISTORY, pathname))) {
      showFooter(true);
    }
  }, [pathname]);

  useEffect(() => {
    // 로그인 상태(redux의 user)가 변했을 때 카트 처리 로직

    // 아래의 조건을 만족할 때는 아무 처리도 하지 않는다.
    // 비로그인 상태이면서 카트에 buyerIdentity가 없는 경우
    if (accessToken === "" && cart.buyerIdentity?.customer === null) return;
    // 로그인 상태이면서 로그인된 유저와 cart의 buyerIndentity가 일치하는 경우
    if (customer?.id !== "" && cart.buyerIdentity?.customer?.id === customer?.id) return;

    // 아래 로직은 로그인 상태와 cart의 buyerIdentity가 일치하지 않을 때 카트를 새로 만들어준다.
    // 1. 로그인
    // 2. 로그아웃
    // 3. redux의 customer id와 cart buyerIndentity의 customer id가 일치하지 않는 경우
    const cartLines = [];

    // 로그인 한 상태일때 (customer가 존재할 때)
    if (cart?.lines?.edges?.length) {
      // 지난 주문 목록을 보고 남은 quantityLimit을 Map으로 저장
      const quantityLimitMap = getQuantityLimitObj({ customer });

      // 새로 만들 카트에 들어가는 line items
      cartLines.push(...adjustCartLines(cart, quantityLimitMap));
    }

    void createCartWithParams({
      variables: {
        input: {
          attributes: [{ key: "isFamilySale", value: "true" }],
          lines: cartLines,
          ...(accessToken && { buyerIdentity: { customerAccessToken: accessToken } }),
        },
      },
    });
  }, [customer, location.href]);

  useLayoutEffect(() => {
    // 체크아웃 페이지인지 확인
    if (Boolean(matchPath(URLS.CHECKOUT, pathname))) return;
    sessionStorage.removeItem("CAPTCHA");

    if (!cart.id) return;

    // 카트 lines 안에 있는 attributes 초기화
    const updateCartLines = [];
    cart.lines?.edges?.forEach(({ node }) => {
      if (node?.attributes?.length) updateCartLines.push({ id: node.id, attributes: [] });
    });

    if (cart.discountCodes.length || updateCartLines.length) {
      void discountMutate({ variables: { cartId: cart.id, discountCodes: [] } }).then(() => {
        // 카트 lines 안에 있는 attributes 초기화
        if (updateCartLines.length) void updateCartLineBase({ variables: { cartId: cart.id, lines: updateCartLines } });
      });
    }
  }, [pathname, customer]);

  useEffect(() => {
    if (accessToken === "") {
      ChannelService.hideChannelButton();
      return;
    }
    ChannelService.boot({
      pluginKey: import.meta.env.VITE_REACT_APP_CHANNEL_TALK_PLUGIN_KEY,
      ...(customer &&
        customer.id && {
          memberId: customer.id.replace(/\D/g, ""),
          profile: {
            name: `${customer.lastName || ""}${customer.firstName || ""}`,
            email: customer.email || "",
            id: customer.id,
          },
        }),
    });
    ChannelService.showChannelButton();

    ChannelService.setPage(location.href);
    ChannelService.track("PageView");
  }, [customer, accessToken, location.href]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search, accessToken]);

  useEffect(() => {
    gtmInit();
  }, []);

  const [isSaleShutDown, setIsSaleShutDown] = useState(false);

  useEffect(() => {
    // getServerTime().then((serverTime) => {
    //   const isArchiveSalePeriod = checkIsArchiveSalePeriod(serverTime);
    //   if (isArchiveSalePeriod) {
    //     // 카트 attribute 확인해서 isFamilySale 없으면 추가해주기
    //     const hasFamilySaleAttribute = (cart?.attributes || [])?.some((attr) => attr.key === "isFamilySale");
    //     if (cart?.id && !hasFamilySaleAttribute) {
    //       setCartAttributes({ variables: { cartId: cart.id, attributes: [{ key: "isFamilySale", value: "true" }] } });
    //     }
    //   } else {
    //     // 세일 종료된 상태에서 카트 상품이 있는 경우 대비하여 카트 비워주기
    //     setIsSaleShutDown(true);
    //     createCart();
    //   }
    //
    //   if (checkAccessAllowed({ customer, currentTime: serverTime })) {
    //     setIsAccessAllowed(true);
    //   }
    // });

    // 카트 attribute 확인해서 isFamilySale 없으면 추가해주기
    const hasFamilySaleAttribute = (cart?.attributes || [])?.some((attr) => attr.key === "isFamilySale");
    if (cart?.id && !hasFamilySaleAttribute) {
      setCartAttributes({ variables: { cartId: cart.id, attributes: [{ key: "isFamilySale", value: "true" }] } });
    }
  }, []);

  const accessAllowed = useMemo(
    () => accessToken && accessToken !== "" && customer?.phone && isAccessAllowed,
    [isAccessAllowed, accessToken, customer],
  );

  return (
    <Routes>
      <Route element={<PageLayout isSaleShutDown={isSaleShutDown} />}>
        <Route element={<NotFound />} path={URLS.PRODUCT_TEST} />

        <Route element={<Login />} path={URLS.LOGIN} />
        {!accessAllowed && <Route path="*" element={<Login />} />}

        {accessAllowed && (
          <>
            <Route element={<Collections isSaleShutDown={isSaleShutDown} />} path={URLS.HOME} />
            <Route element={<Account />} path={URLS.ACCOUNT}>
              <Route element={<AccountInformation />} path={URLS.ACCOUNT_PERSONAL_INFORMATION} />
              <Route element={<OrderDetail />} path={URLS.ACCOUNT_ORDER_DETAIL} />
              <Route element={<OrderHistory />} path={URLS.ACCOUNT_ORDER_HISTORY} />
            </Route>
            {!isSaleShutDown && <Route element={<Checkout />} path={URLS.CHECKOUT} />}
            <Route element={<OrderCompleted />} path={URLS.ORDER_COMPLETED} />
            <Route element={<Collections isSaleShutDown={isSaleShutDown} />} path={URLS.COLLECTIONS_DETAIL} />
            <Route element={<Products isSaleShutDown={isSaleShutDown} />} path={URLS.PRODUCTS_DETAIL} />
            <Route element={<Search isSaleShutDown={isSaleShutDown} />} path={URLS.SEARCH} />
          </>
        )}
        <Route element={<SubPages />} path={URLS.PAGES}>
          <Route element={<Cs />} path={URLS.PAGES_CS} />
          <Route element={<ShippingInformation />} path={URLS.PAGES_SHIPPING_INFORMATION} />
          <Route element={<PrivacyPolicy />} path={URLS.PAGES_PRIVACY_POLICY} />
          <Route element={<TermsConditions />} path={URLS.PAGES_TERMS_AND_CONDITIONS} />
        </Route>

        <Route element={<NotFound />} path={URLS.NOT_FOUND} />
        <Route element={<NotFound />} path="*" />
        <Route element={<KakaoOauth />} path={URLS.KAKAO_OAUTH} />
        <Route element={<Refresh />} path={URLS.REFRESH} />
      </Route>
    </Routes>
  );
}

export default App;
